import React, {
  cloneElement,
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import block from 'bem-cn'

import useEventListener from 'shared/hooks/useEventListener'
import { PopupControls, usePopupControls } from 'shared/hooks/usePopupControls'

import './Dropdown.scss'

export enum DropdownVerticalPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
}

export enum DropdownHorizontalPosition {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

export enum DropdownAnimationVariant {
  NONE = 'none',
  TOP = 'top',
  BOTTOM = 'bottom',
}

export enum DropdownPositionStrategy {
  ABSOLUTE = 'absolute',
  FIXED = 'fixed',
}

export type DropdownProps = PropsWithChildren<{
  className?: string
  dropdownClassName?: string
  horizontalPosition?: DropdownHorizontalPosition
  verticalPosition?: DropdownVerticalPosition
  positionStrategy?: DropdownPositionStrategy
  fullWidth?: boolean
  withShadow?: boolean
  animation?: DropdownAnimationVariant
  container?: HTMLElement
  trigger?: React.ReactElement
  triggerElement?: HTMLElement | null
  gap?: number
  popupControls?: PopupControls
  onChangeVerticalPosition?: (position: DropdownVerticalPosition) => void
}>

const b = block('dropdown')

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      className,
      fullWidth,
      dropdownClassName,
      horizontalPosition,
      verticalPosition,
      positionStrategy = DropdownPositionStrategy.ABSOLUTE,
      popupControls,
      trigger,
      container,
      gap = 5,
      withShadow = true,
      onChangeVerticalPosition,
      animation = DropdownAnimationVariant.BOTTOM,
      triggerElement,
      children,
    },
    ref,
  ) => {
    const isFixedStrategy = positionStrategy === DropdownPositionStrategy.FIXED
    const dropdownRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLElement | undefined>(null)
    const currentTriggerElement = trigger ? triggerRef.current : triggerElement
    const [styles, setStyles] = useState<CSSProperties>({})
    const controls = usePopupControls()
    const currentControls = popupControls ?? controls

    const calculateStyles = useCallback(() => {
      if (!currentTriggerElement || !dropdownRef.current) {
        return
      }

      const triggerRect = currentTriggerElement.getBoundingClientRect()
      const dropdownRect = dropdownRef.current.getBoundingClientRect()
      const dropdownHeight = dropdownRect.height || 350
      const dropdownWidth = dropdownRect.width || 268
      const dropdownStyles: CSSProperties = {}

      if (isFixedStrategy) {
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight
        const viewportWidth = window.visualViewport?.width ?? window.innerWidth
        const { left: triggerLeft, right: triggerRight, top: triggerTop, bottom: triggerBottom, width: triggerWidth } =
          triggerRect
        const spaceBelow = viewportHeight - triggerBottom - gap
        const spaceAbove = triggerTop - gap

        let openOnTop = false

        if (verticalPosition === DropdownVerticalPosition.TOP) {
          openOnTop = true
        } else if (verticalPosition === DropdownVerticalPosition.BOTTOM) {
          openOnTop = false
        } else {
          openOnTop = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
        }

        onChangeVerticalPosition?.(openOnTop ? DropdownVerticalPosition.TOP : DropdownVerticalPosition.BOTTOM)

        let top = openOnTop ? triggerTop - dropdownHeight - gap : triggerBottom + gap
        const maxTop = viewportHeight - dropdownHeight - gap
        top = Math.max(gap, Math.min(top, maxTop))

        let left = triggerLeft

        if (horizontalPosition === DropdownHorizontalPosition.RIGHT) {
          left = triggerRight - dropdownWidth
        } else if (horizontalPosition === DropdownHorizontalPosition.CENTER) {
          left = triggerLeft + ((triggerWidth - dropdownWidth) / 2)
        }

        left = Math.max(gap, Math.min(left, viewportWidth - dropdownWidth - gap))

        dropdownStyles.position = 'fixed'
        dropdownStyles.top = `${top}px`
        dropdownStyles.left = `${left}px`
        dropdownStyles.right = 'auto'
        dropdownStyles.bottom = 'auto'
        dropdownStyles.zIndex = 1000
        dropdownStyles.maxHeight = `${viewportHeight - (gap * 2)}px`
        dropdownStyles.overflowY = 'auto'

        setStyles(dropdownStyles)
        return
      }

      const containerRect = container
        ? container.getBoundingClientRect()
        : { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
      const viewportHeight = containerRect.height
      const viewportWidth = containerRect.width

      if (
        verticalPosition === DropdownVerticalPosition.TOP ||
        (verticalPosition !== DropdownVerticalPosition.BOTTOM &&
          triggerRect.bottom + dropdownHeight > viewportHeight)
      ) {
        onChangeVerticalPosition?.(DropdownVerticalPosition.TOP)
        dropdownStyles.bottom = `${triggerRect.height + gap}px`
      } else {
        onChangeVerticalPosition?.(DropdownVerticalPosition.BOTTOM)
        dropdownStyles.top = `${triggerRect.height + gap}px`
      }

      if (horizontalPosition === DropdownHorizontalPosition.LEFT) {
        dropdownStyles.left = '0'
      } else if (horizontalPosition === DropdownHorizontalPosition.RIGHT) {
        dropdownStyles.right = '0'
      } else if (horizontalPosition === DropdownHorizontalPosition.CENTER) {
        const diffWidth = triggerRect.width - dropdownWidth

        dropdownStyles.left = `${diffWidth / 2}px`
      } else if (triggerRect.left + dropdownWidth > viewportWidth) {
        dropdownStyles.right = '0'
      } else {
        dropdownStyles.left = '0'
      }

      setStyles(dropdownStyles)
    }, [
      container,
      currentTriggerElement,
      gap,
      horizontalPosition,
      isFixedStrategy,
      onChangeVerticalPosition,
      verticalPosition,
    ])

    useEffect(() => {
      let frameId: number | undefined

      if (currentControls.isOpened) {
        calculateStyles()
        frameId = window.requestAnimationFrame(calculateStyles)
      }

      return () => {
        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId)
        }
      }
    }, [calculateStyles, currentControls.isOpened])

    useEventListener('blur', () => {
      currentControls.closePopup?.()
    })

    useEventListener('click', e => {
      if (
        currentTriggerElement &&
        !currentTriggerElement.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        currentControls.closePopup?.()
      }
    })

    useEventListener('mousedown', e => {
      if (
        currentTriggerElement &&
        !currentTriggerElement.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        currentControls.closePopup?.()
      }
    })

    useEventListener('touchstart', e => {
      if (
        currentTriggerElement &&
        !currentTriggerElement.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        currentControls.closePopup?.()
      }
    })

    useEventListener('resize', () => {
      if (currentControls.isOpened) {
        calculateStyles()
      }
    })

    useEffect(() => {
      if (!currentControls.isOpened || !isFixedStrategy) {
        return undefined
      }

      const handleScroll = () => {
        calculateStyles()
      }

      window.addEventListener('scroll', handleScroll, true)

      return () => {
        window.removeEventListener('scroll', handleScroll, true)
      }
    }, [calculateStyles, currentControls.isOpened, isFixedStrategy])

    useEffect(() => {
      let observer: ResizeObserver

      if (currentControls.isOpened) {
        observer = new ResizeObserver(calculateStyles)

        if (currentTriggerElement) {
          observer.observe(currentTriggerElement)
        }

        if (container) {
          observer.observe(container)
        }
      }

      return () => {
        observer?.disconnect()
      }
    }, [calculateStyles, container, currentControls.isOpened, currentTriggerElement])

    useImperativeHandle(ref, () => dropdownRef.current as HTMLDivElement)

    const renderDropdownContent = () => (
      <div
        ref={dropdownRef}
        className={b('content', { fullWidth, withShadow, animation, fixed: isFixedStrategy }).mix(dropdownClassName)}
        style={styles}>
        {children}
      </div>
    )

    if (!trigger) {
      if (!currentControls.isOpened) {
        return null
      }

      if (isFixedStrategy) {
        return createPortal(renderDropdownContent(), document.body)
      }

      return renderDropdownContent()
    }

    return (
      <div className={b('wrapper', { fullWidth }).mix(className)}>
        {trigger && cloneElement(trigger, { ref: triggerRef, onClick: currentControls.togglePopup, fullWidth })}
        {currentControls.isOpened &&
          (isFixedStrategy
            ? createPortal(renderDropdownContent(), document.body)
            : renderDropdownContent())}
      </div>
    )
  },
)

Dropdown.displayName = 'Dropdown'
